import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import type { RotateScreenModalProps, RotateScreenModalRef } from './RotateScreenModal.data';
import styles from './RotateScreenModal.module.scss';
import {
  setupLoopingTimeline,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './RotateScreenModal.transitions';
import Heading from '../../atoms/heading/Heading';
import { useTransitionController } from 'util/react-transition-component';
import type { DeviceOrientationContextType } from '../../unlisted/DeviceOrientation';
import { useDeviceOrientationContext } from '../../unlisted/DeviceOrientation';
import type { HeadingRef } from '../../atoms/heading/Heading.data';
import useSetupTimeline from '../../../hooks/useSetupTimeline';

export default function RotateScreenModal({
  className,
  ref,
}: RotateScreenModalProps & { ref?: Ref<RotateScreenModalRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const phoneRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HeadingRef>(null);
  const loopingTimeline = useRef<gsap.core.Timeline>(undefined);
  const [isVisible, setIsVisible] = useState(false);

  const phoneController = useTransitionController(
    () => ({
      ref: phoneRef,
      refs: {
        phoneRef,
        elementRef,
        titleRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const { isMobileLandscape } = useDeviceOrientationContext() as DeviceOrientationContextType;

  loopingTimeline.current = useSetupTimeline(setupLoopingTimeline, {
    phoneRef,
  });

  useEffect(
    () => {
      if (isMobileLandscape) {
        setIsVisible(true);
        phoneController.transitionIn();
        loopingTimeline?.current?.play();
      } else {
        phoneController.transitionOut().then(() => {
          setIsVisible(false);
        });
        loopingTimeline?.current?.pause();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobileLandscape],
  );

  return (
    <div
      ref={elementRef}
      className={classNames(styles.rotateScreenModal, className, {
        [styles.isShown]: isVisible,
      })}
    >
      <div className={styles.centerContainer}>
        <div className={styles.phoneContainer} ref={phoneRef}>
          <Image
            src="/assets/svg/phone.svg"
            height={80}
            width={40}
            className={styles.phone}
            alt="phone outline"
          />
        </div>
        <Heading
          ref={titleRef}
          as="h5"
          size="heading-05"
          className={styles.title}
          disableTriggerInOnScroll={false}
          transitionInType="splitWordsSlideInUp"
          transitionOutType="splitWordsSlideOutUp"
        >
          Please rotate device
        </Heading>
      </div>
    </div>
  );
}
